import { useEffect, useMemo, useState } from 'react';
import { Card, LinkButton, Tooltip } from '@pmi/dsm-react';
import { FieldMetaProps, Formik } from 'formik';
import { Spinner, Button } from 'react-bootstrap';
import {
  AlertQuestionIcon,
  DotIcon,
} from '@pmi/dsm-react/dist/components/icons/fa/16';
import { useGetDealsListQuery } from '../../../shared/store/shared/dealsApiSlice';
import styles from './agreementSettings.module.scss';
import ResponsiveTable from '../../../shared/components/ResponsiveTable';
import { SortedColumn } from '../../../shared/components/ResponsiveTable/models';
import SettingsToggle from './SettingsToggle';
import { toastWithAnalytics } from '../../../shared/analytics';
import { useUpdateDealSettingsMutation } from './apiSlice';
import { ErrorResponse } from '../../../shared/models/ErrorResponse';
import usePermissions from '../../../shared/hooks/usePermissions';
import PERMISSIONS from '../../../shared/permissions';
import AgreementComments from './AgreementComments';

export interface AgreementSettingsProps {
  accountId: string;
}

const getDifferenceOnly = (values: any, initialValues: any) => {
  if (Array.isArray(values)) {
    // if you got array
    return values; // just copy it
  }
  // if you got object
  const rst = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const k in values) {
    // visit all fields
    if (typeof initialValues[k] === 'object') {
      // if field contains object (or array because arrays are objects too)
      rst[k] = getDifferenceOnly(values[k], initialValues[k]); // diff the contents
    } else if (initialValues[k] !== values[k]) {
      // if field is not an object and has changed
      rst[k] = values[k]; // use new value
    }
    // otherwise just skip it
  }
  return rst;
};

const tableColumns = (
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  setFieldTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean,
  ) => void,
  getFieldMeta: (name: string) => FieldMetaProps<any>,
  isDisabled: boolean,
  accountId: string,
  dealSettingsChanged?: any,
): SortedColumn[] => [
  {
    name: 'agreement',
    title: 'Agreement',
    cell: (cntx) => {
      const agreementSettingChanged = dealSettingsChanged
        ? Object.keys(dealSettingsChanged?.[cntx.row.original?.id]).length > 0
        : false;

      return (
        <div
          className={
            agreementSettingChanged
              ? 'fw-bold d-flex align-items-center position-relative flex-wrap'
              : null
          }
        >
          {agreementSettingChanged && <DotIcon className={styles.smallDot} />}
          <span>{cntx.row.original?.id}</span>
          <p className="mb-0 d-block w-100">{cntx.row.original?.dealName}</p>
        </div>
      );
    },

    width: 10,
    sortingEnabled: false,
  },
  {
    name: 'comments',
    title: '',
    cell: (cntx) => (
      <AgreementComments deal={cntx.row.original} accountId={accountId} />
    ),
    width: 1,
    sortingEnabled: false,
  },
  {
    name: 'activation',
    title: 'Activation',
    tooltip: (
      <Tooltip
        type="sm"
        content="Set whether the Agreement is activated for User enrollment."
        placement="auto"
        className="ps-0 pe-0 ms-1 darkTooltip"
      >
        <AlertQuestionIcon
          color="neutral"
          fontSize="xs"
          className="cursor-pointer"
        />
      </Tooltip>
    ),
    cell: (cntx) => (
      <SettingsToggle
        key={cntx.row.original?.id}
        deal={cntx.row.original}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
        getFieldMeta={getFieldMeta}
        isDisabled={isDisabled}
        type="enabled"
        status={dealSettingsChanged}
      />
    ),
    width: 1,
    sortingEnabled: false,
  },
  {
    name: 'visibility',
    title: 'Visibility',
    tooltip: (
      <Tooltip
        type="sm"
        content="Set whether the Agreement is visible for External Admin management."
        placement="auto"
        className="ps-0 pe-0 ms-1 darkTooltip"
      >
        <AlertQuestionIcon
          color="neutral"
          fontSize="xs"
          className="cursor-pointer"
        />
      </Tooltip>
    ),
    cell: (cntx) => (
      <SettingsToggle
        key={cntx.row.original?.id}
        deal={cntx.row.original}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
        getFieldMeta={getFieldMeta}
        isDisabled={isDisabled}
        type="hidden"
        status={dealSettingsChanged}
      />
    ),
    width: 1,
    sortingEnabled: false,
  },
];

export default function AgreementSettings({
  accountId,
}: AgreementSettingsProps) {
  const { hasAccess } = usePermissions();
  const isInternalAdmin = hasAccess({
    permissions: [PERMISSIONS.ManageElevatedAdmins],
  });
  const {
    data: dealList = [],
    isLoading,
    isFetching,
  } = useGetDealsListQuery(accountId, { skip: !isInternalAdmin });
  const [updateDealSettings, { isLoading: isUpdating }] =
    useUpdateDealSettingsMutation();
  const [dealSettingsChanged, setDealSettingsChanged] = useState(undefined);
  const initialValues = useMemo(
    () =>
      dealList
        ? Object.assign(
            {},
            ...dealList.map((deal) => ({
              [deal.id]: {
                enabled: deal.enabled,
                hidden: deal.hidden,
                reseller: deal.reseller,
              },
            })),
          )
        : [],
    [dealList],
  );

  const [dealStatusTogglePermission, setDealStatusTogglePermission] =
    useState(false);

  const hasToggleColumns = () => {
    // only internal admin can see the toggle page
    if (isInternalAdmin) {
      return true;
    }

    if (!dealStatusTogglePermission) {
      return false;
    }

    if (!dealList?.some((d) => d.channelEngagement)) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!isLoading) {
      setDealStatusTogglePermission(hasToggleColumns());
    }
  }, [isLoading]);

  if (isLoading) {
    return (
      <div className="col-12">
        <div className="d-block ps-3">
          <Spinner />
        </div>
      </div>
    );
  }

  if (
    dealList?.length < 1 ||
    (!dealStatusTogglePermission && !dealList?.some((x) => x.channelEngagement))
  ) {
    return null;
  }

  return hasToggleColumns() ? (
    <div className="col-12">
      <Card className="p-0">
        <div className="p-4">
          <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);
              const difference = getDifferenceOnly(values, initialValues);
              const changedDealIds = Object.keys(difference).filter(
                (x) => Object.keys(difference[x]).length > 0,
              );

              const payload = changedDealIds.map((dealId) => ({
                dealId,
                ...difference[dealId],
              }));

              try {
                await updateDealSettings({ accountId, payload }).unwrap();
                setDealSettingsChanged(difference);
                toastWithAnalytics(
                  `${changedDealIds.join(', ')} updated successfully`,
                  'success',
                );
              } catch ({ data }) {
                const msg = data
                  ? (data as ErrorResponse)?.detail
                  : `Cannot update the agreement ${changedDealIds.join(', ')}`;
                toastWithAnalytics(msg, 'danger');
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              handleSubmit,
              setFieldValue,
              setFieldTouched,
              dirty,
              getFieldMeta,
              resetForm,
              isSubmitting,
            }) => (
              <>
                <div className="d-flex justify-content-between flex-wrap mb-2">
                  <div className="me-2">
                    <h4 className="fw-bold mb-0">Agreement Toggles</h4>
                    <p className="mb-0">
                      Configure agreement activation and display permissions
                    </p>
                  </div>
                  {dirty && !(isUpdating || isFetching) && (
                    <div className="d-flex flex-wrap align-items-start">
                      <LinkButton
                        titleText="Cancel Changes"
                        onClick={resetForm}
                        className="me-2"
                        disabled={isSubmitting}
                      />
                      <Button
                        disabled={isSubmitting}
                        onClick={(e: any) => {
                          e.preventDefault();
                          handleSubmit(e);
                        }}
                      >
                        Save Changes
                      </Button>
                    </div>
                  )}
                </div>
                <div className={styles.settingsTableContainer}>
                  <ResponsiveTable
                    data={dealList}
                    className={styles.settingsTable}
                    columns={tableColumns(
                      setFieldValue,
                      setFieldTouched,
                      getFieldMeta,
                      isSubmitting || isFetching,
                      accountId,
                      dealSettingsChanged,
                    ).filter((x) => {
                      if (!isInternalAdmin && x.name === 'comments') {
                        return false;
                      }
                      if (
                        !dealStatusTogglePermission &&
                        (x.name === 'activation' || x.name === 'visibility')
                      ) {
                        return false;
                      }
                      if (
                        x.name === 'reseller' &&
                        !dealList.some((d) => d.channelEngagement)
                      )
                        return false;

                      return true;
                    })}
                    onSort={() => {}}
                  />
                </div>
              </>
            )}
          </Formik>
        </div>
      </Card>
    </div>
  ) : null;
}
